import { t } from 'ttag';

/*
  Registry describing the "Select what to duplicate" checklist.

  Each leaf's `path` is the dotted key into the BE `duplication_config` payload
  (flux .../entities/duplication_config.py). `configKey` is the source-event
  configuration key that gates availability: when the source event doesn't have
  that feature, there is nothing to copy, so the row/child is shown disabled.
  A node with no `configKey` is always available. A child inherits its
  ancestors' `configKey` gates in addition to its own.
*/

export const UNAVAILABLE_TOOLTIP = () =>
  t`This feature is not available for the source event or new event.`;

export const EVENT_DETAILS_TOOLTIP = () =>
  t`Event details are duplicated automatically. You can change them later.`;

export const buildModules = () => [
  {
    id: 'privacy_and_terms',
    label: t`Privacy Policy & Terms of Use`,
    path: 'privacy_and_terms',
  },
  {
    id: 'libraries',
    label: t`Libraries`,
    children: [
      {
        id: 'libraries.people',
        label: t`People`,
        path: 'libraries.people',
        info: t`Only Groups and Custom Fields are copied.`,
      },
      {
        id: 'libraries.companies',
        label: t`Companies`,
        path: 'libraries.companies',
        configKey: 'is_companies_enabled',
      },
      {
        id: 'libraries.sessions',
        label: t`Sessions`,
        path: 'libraries.sessions',
        configKey: 'is_sessions_enabled',
      },
      {
        id: 'libraries.videos',
        label: t`Videos`,
        path: 'libraries.videos',
        configKey: 'is_videos_enabled',
        info: t`Only Groups are copied.`,
      },
      {
        id: 'libraries.documents',
        label: t`Documents`,
        path: 'libraries.documents',
        configKey: 'is_document_upload_enabled',
      },
      {
        id: 'libraries.maps',
        label: t`Maps`,
        path: 'libraries.maps',
        configKey: 'is_interactive_maps_enabled',
      },
      { id: 'libraries.banners', label: t`Banners`, path: 'libraries.banners' },
    ],
  },
  {
    id: 'website',
    label: t`Website`,
    path: 'website',
    configKey: 'is_website_builder_enabled',
  },
  {
    id: 'email_campaigns',
    label: t`Email Campaigns`,
    hint: t`Templates Only`,
    path: 'email_campaigns',
    configKey: 'is_communications_enabled',
  },
  {
    id: 'registration',
    label: t`Registration`,
    hint: t`Details`,
    path: 'registration.enabled',
    configKey: 'is_registration_v2_enabled',
    children: [
      { id: 'registration.content', label: t`Form & Tickets`, path: 'registration.content' },
      { id: 'registration.promo_codes', label: t`Promo Codes`, path: 'registration.promo_codes' },
      { id: 'registration.emails', label: t`Emails`, path: 'registration.emails' },
    ],
  },
  {
    id: 'event_app',
    label: t`Event App`,
    hint: t`Settings | Design Studio | Audience Response`,
    path: 'event_app.enabled',
    configKey: 'is_eventspace_enabled',
    children: [
      { id: 'event_app.menu_sections', label: t`Menu Sections`, path: 'event_app.menu_sections' },
      {
        id: 'event_app.group_discussions',
        label: t`Group Discussions`,
        path: 'event_app.group_discussions',
        configKey: 'is_group_discussions_enabled',
      },
      {
        id: 'event_app.surveys',
        label: t`Surveys`,
        path: 'event_app.surveys',
        configKey: 'is_survey_enabled',
      },
      {
        id: 'event_app.page_designer',
        label: t`Page Designer`,
        path: 'event_app.page_designer',
        info: t`Only Widget Pages and Homepage assignment are copied.`,
      },
      {
        id: 'event_app.gamification',
        label: t`Gamification`,
        path: 'event_app.gamification',
        configKey: 'is_gamify_app_enabled',
      },
    ],
  },
  {
    id: 'badges',
    label: t`Badges & Badges Templates`,
    path: 'badges',
    configKey: 'badge_designer_enabled',
  },
  {
    id: 'mobi_ai',
    label: t`MobiAI`,
    configKey: 'is_askmobi_feature_enabled',
    children: [
      { id: 'mobi_ai.enabled', label: t`Global Settings`, path: 'mobi_ai.enabled' },
      {
        id: 'mobi_ai.registration_concierge',
        label: t`Registration Concierge`,
        path: 'mobi_ai.registration_concierge',
      },
    ],
  },
];

/*
  Dependency rules the UI must never let the user violate — the BE 400s
  otherwise (duplication_config.py `_DEPENDENCY_RULES`). A dependent leaf is
  only selectable while its prerequisite leaf is selected.
*/
const DEPENDENCY_RULES = [
  { path: 'registration.content', requires: 'libraries.people' },
  { path: 'event_app.menu_sections', requires: 'event_app.page_designer' },
  { path: 'event_app.surveys', requires: 'libraries.sessions' },
];

// Dependent leaf path -> prerequisite leaf path.
export const REQUIRES_BY_PATH = DEPENDENCY_RULES.reduce(
  (acc, rule) => ({ ...acc, [rule.path]: rule.requires }),
  {},
);

// Depth-first visit of the module tree; `visit(node, ancestors)` per node.
export const walkModules = (nodes, visit, ancestors = []) => {
  nodes.forEach(node => {
    visit(node, ancestors);
    if (node.children) walkModules(node.children, visit, [...ancestors, node]);
  });
};

// Every payload leaf path, with the ordered configKey gates (own + ancestors').
export const leafGates = modules => {
  const gates = {};
  walkModules(modules, (node, ancestors) => {
    if (!node.path) return;
    gates[node.path] = [...ancestors, node].map(n => n.configKey).filter(Boolean);
  });
  return gates;
};

const allLeafPaths = modules => Object.keys(leafGates(modules));

export const labelByPath = modules => {
  const labels = {};
  walkModules(modules, node => {
    if (node.path) labels[node.path] = node.label;
  });
  return labels;
};

// Flat all-selected default keyed by payload path.
export const defaultSelection = modules =>
  allLeafPaths(modules).reduce((acc, path) => ({ ...acc, [path]: true }), {});

// Serialize a flat { path: bool } map into the nested duplication_config body.
export const toDuplicationConfig = selection =>
  Object.entries(selection).reduce((config, [path, value]) => {
    const parts = path.split('.');
    let cursor = config;
    parts.slice(0, -1).forEach(part => {
      cursor[part] = cursor[part] || {};
      cursor = cursor[part];
    });
    cursor[parts[parts.length - 1]] = value;
    return config;
  }, {});
